import { productEvent } from "@shared/analytics/productEvents";
import {
    useProductJourney,
    useProductObservation
} from "@shared/analytics/useProductJourney";
import { formatCurrency } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import type { StatBandStat } from "@solomon-labs/design/components/layout";
import {
    Heading,
    Skeleton,
    TabsMenu,
    TabTrigger,
    Text
} from "@solomon-labs/design/components/ui";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
    reportingCoverage,
    reportingReserveClasses
} from "@/hooks/stats/reportingReserves";
import {
    useReportingBacking,
    useReportingToken
} from "@/hooks/stats/useJachinReporting";
import { useReportingApyHistory } from "@/hooks/stats/useReportingApyHistory";
import { StatsHeader } from "@/pages/Stats/components/StatsHeader";
import { YieldContent } from "@/pages/Stats/components/YieldContent";

import { StablecoinComposition } from "./components/ReserveComposition";
import { ReservesEmptyState } from "./components/ReservesEmptyState";
import { ReservesSummary } from "./components/ReservesSummary";
import { SecurityContent } from "./components/SecurityContent";

type YieldRange = "7d" | "30d" | "90d" | "1y" | "all";
const YIELD_RANGE_PREFERENCE: readonly YieldRange[] = [
    "30d",
    "90d",
    "1y",
    "all"
];
const MINIMUM_RANGE_POINTS = 5;

type StatsV4Tab = "yield" | "reserves" | "security";
/**
 * Each subtitle names what the tab actually holds, in the tab's own nouns, so
 * a reader can tell which one answers their question without opening all
 * three. No adjectives: nothing here is "comprehensive" or "full", and a
 * transparency page that oversells is the one nobody believes.
 */
const TABS: ReadonlyArray<{
    key: StatsV4Tab;
    title: string;
    subtitle: string;
}> = [
    {
        key: "reserves",
        title: "Reserves",
        subtitle: "Backing and Supply"
    },
    {
        key: "yield",
        title: "Rewards",
        subtitle: "Earnings and Distributions"
    },
    {
        key: "security",
        title: "Security",
        subtitle: "Controls and Resources"
    }
];

/**
 * URL slug for each tab, so a tab is a shareable address: /stats/reserves,
 * /stats/rewards, /stats/security. The slug follows the label a reader sees,
 * which is why "yield" is addressed as "rewards" — the internal key predates
 * the rename and is not worth churning the data hooks over.
 */
const TAB_SLUG: Record<StatsV4Tab, string> = {
    reserves: "reserves",
    yield: "rewards",
    security: "security"
};

const SLUG_TAB: ReadonlyMap<string, StatsV4Tab> = new Map(
    (Object.keys(TAB_SLUG) as StatsV4Tab[]).map((key) => [TAB_SLUG[key], key])
);

/** Bare /stats, and any slug that is not one of the three, land here. */
const DEFAULT_TAB: StatsV4Tab = "reserves";

export function CurrentStatsV4Page(): React.ReactElement {
    const navigate = useNavigate();
    const [yieldRange, setYieldRange] = React.useState<YieldRange>("30d");
    const yieldData = useReportingApyHistory(yieldRange);

    // The counts arrive with the distributions, so the opening range can only
    // be corrected once they land. Widening happens at most once per dataset.
    const rangeCounts = yieldData.pointsByPeriod;
    React.useEffect(() => {
        if (rangeCounts[yieldRange] >= MINIMUM_RANGE_POINTS) return;
        const wider = YIELD_RANGE_PREFERENCE.find(
            (candidate) => rangeCounts[candidate] >= MINIMUM_RANGE_POINTS
        );
        if (wider && wider !== yieldRange) setYieldRange(wider);
    }, [rangeCounts, yieldRange]);

    // The URL owns which tab is open, so the address bar, a shared link and
    // the back button all agree. An unknown slug renders the default rather
    // than 404ing: the page is still the answer to "show me the stats".
    const { tab: tabSlug } = useParams();
    const activeTab: StatsV4Tab =
        (tabSlug === undefined ? undefined : SLUG_TAB.get(tabSlug)) ??
        DEFAULT_TAB;
    const analytics = useProductJourney("stats", "stats_page");
    useProductObservation(analytics, "viewed", { tab: TAB_SLUG[activeTab] });
    const tokenQuery = useReportingToken();
    const backingQuery = useReportingBacking();
    const reservesLoading = backingQuery.isLoading;
    const classes = React.useMemo(
        () => reportingReserveClasses(backingQuery.data?.data),
        [backingQuery.data]
    );
    const showReserveSummary = reservesLoading || classes.length > 0;
    // No reserve classes once loading ends (the call failed or came back
    // empty): the tab is the empty-state illustration and its text alone,
    // as the Rewards tab is with no distributions.
    const reservesUnavailable = !reservesLoading && classes.length === 0;
    const token = tokenQuery.data?.data;
    const summaryStats: StatBandStat[] = [
        {
            label: "Total USDv Supply",
            value: tokenQuery.isLoading ? (
                <Skeleton className="h-7 w-32" />
            ) : token ? (
                formatCurrency(token.totalSupplyUi)
            ) : (
                "—"
            )
        },
        ...(token?.enrolledInEarnUi !== undefined &&
        token.enrolledInEarnUi !== null &&
        new BigDecimal(token.enrolledInEarnUi).gt(0)
            ? [
                  {
                      label: "Amount of Supply Earning",
                      value: formatCurrency(token.enrolledInEarnUi)
                  }
              ]
            : []),
        {
            label: "Backing Collateral",
            value: reportingCoverage(
                tokenQuery.isError ? undefined : tokenQuery.data,
                backingQuery.isError ? undefined : backingQuery.data
            )
        }
    ];
    return (
        <main className="flex-1 flex flex-col items-center px-4 py-6 md:px-6 md:py-10 text-foreground-base">
            <div className="w-full max-w-5xl flex flex-col gap-10 md:gap-12">
                <section>
                    <StatsHeader />
                    <div className="h-px w-full bg-line-neutral" />

                    <TabsMenu
                        value={activeTab}
                        onValueChange={(value) => {
                            productEvent("stats:tab_selected", {
                                entry_point: "stats_page",
                                tab: TAB_SLUG[value as StatsV4Tab],
                                previous_tab: TAB_SLUG[activeTab]
                            });
                            navigate(`/stats/${TAB_SLUG[value as StatsV4Tab]}`);
                        }}
                        align="start"
                        fullWidth
                        indicatorPosition="top"
                    >
                        {/* A stacked label is not what TabTrigger's fixed
                            height and `leading-none` are shaped for, so the
                            box grows and the lines get their leading back.
                            Design 0.4.0 sizes are responsive (`h-11
                            md:h-12`), so the override needs its md: half too
                            or the fixed height returns from 768px up. */}
                        {TABS.map((tab) => (
                            <TabTrigger
                                key={tab.key}
                                value={tab.key}
                                size="lg"
                                interaction="fillFromTop"
                                className="h-auto py-3 md:h-auto"
                            >
                                <span className="flex flex-col items-start gap-0.5 leading-normal">
                                    <span>{tab.title}</span>
                                    <span className="hidden md:block font-sans text-sm text-foreground-muted text-left">
                                        {tab.subtitle}
                                    </span>
                                </span>
                            </TabTrigger>
                        ))}
                    </TabsMenu>
                    <div className="h-px w-full bg-line-neutral" />

                    <div className="pt-12">
                        {activeTab === "reserves" &&
                            (reservesUnavailable ? (
                                <ReservesEmptyState />
                            ) : (
                                <div className="flex flex-col gap-8 md:gap-10">
                                    <section
                                        aria-label="USDv reserves"
                                        data-motion-section
                                        style={{
                                            opacity: 0,
                                            animation:
                                                "page-fade-up 360ms ease-out 0ms forwards"
                                        }}
                                    >
                                        {/* `Heading`, not `SectionHeading`: the
                                            rule under this title is drawn by the
                                            StatBand below it, and `SectionHeading`
                                            always draws one of its own, so the two
                                            would stack. `Heading`'s divider is
                                            opt-in and stays off. The padding is the
                                            clearance the design system gives an
                                            h1-scale title above a rule
                                            (`SectionHeading`'s own h1 figure), kept
                                            here because the rule still exists — it
                                            is just owned by the next element. */}
                                        {showReserveSummary && (
                                            <Heading
                                                title={
                                                    <span id="usdv-supply-heading">
                                                        USDv Supply
                                                    </span>
                                                }
                                                className="pb-2 md:pb-5"
                                            />
                                        )}

                                        <div className="flex flex-col gap-6">
                                            {showReserveSummary && (
                                                <ReservesSummary
                                                    stats={summaryStats}
                                                />
                                            )}
                                            {backingQuery.data?.data?.holdings.some(
                                                (h) => h.amountUsd === null
                                            ) && (
                                                <Text variant="small">
                                                    Some reserve assets have no
                                                    USD price. Total coverage is
                                                    unavailable.
                                                </Text>
                                            )}

                                            {backingQuery.data?.data?.holdings
                                                .filter(
                                                    (h) => h.amountUsd === null
                                                )
                                                .map((h) => (
                                                    <div
                                                        key={`${h.mint}:${h.vaultAddress}`}
                                                    >
                                                        <Text variant="small">
                                                            {h.symbol}:{" "}
                                                            {h.amount} tokens.
                                                            USD value
                                                            unavailable.
                                                        </Text>
                                                        {h.vaultAddress && (
                                                            <a
                                                                href={`https://solscan.io/account/${encodeURIComponent(h.vaultAddress)}`}
                                                                onClick={() =>
                                                                    productEvent(
                                                                        "stats:link_clicked",
                                                                        {
                                                                            entry_point:
                                                                                "stats_page",
                                                                            section:
                                                                                "reserves",
                                                                            link_type:
                                                                                "vault_explorer"
                                                                        }
                                                                    )
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <Text variant="small">
                                                                    Verify
                                                                    reserve
                                                                    account
                                                                </Text>
                                                            </a>
                                                        )}
                                                    </div>
                                                ))}
                                            <StablecoinComposition
                                                classes={classes}
                                                loading={reservesLoading}
                                            />
                                        </div>
                                    </section>
                                </div>
                            ))}
                        {activeTab === "security" && <SecurityContent />}
                        {activeTab === "yield" && (
                            <div className="flex flex-col gap-8 md:gap-10">
                                <YieldContent
                                    apyValue={yieldData.apyValue}
                                    liveApy={yieldData.liveApy}
                                    apyChange={yieldData.apyChange}
                                    apyPeriodLabel={yieldData.apyPeriodLabel}
                                    chartPoints={yieldData.chartPoints}
                                    chartTimestamps={yieldData.chartTimestamps}
                                    chartLabels={yieldData.chartLabels}
                                    allTimePoints={
                                        yieldData.allTimeApyHistory.length
                                    }
                                    pointsByRange={yieldData.pointsByPeriod}
                                    chartStartLabel={yieldData.chartStartLabel}
                                    chartEndLabel={yieldData.chartEndLabel}
                                    loading={false}
                                    range={yieldRange}
                                    onRangeChange={setYieldRange}
                                    headlineValueVariant="number-lg"
                                />
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </main>
    );
}
